import type {CSSProperties, ReactNode} from 'react';
import {useRef, useState} from 'react';
import {Box} from '@mantine/core';
import {useVirtualizer} from '@tanstack/react-virtual';

/**
 * Windowed table via `@tanstack/react-virtual` — div-based (ARIA table roles restore
 * semantics); `onRowClick` is an opt-in escape hatch for touch/tablet flows only.
 * @registry Virtualized table for large datasets, no built-in sort/filter/pagination.
 */
export interface VirtualizedTableColumn<T> {
  id: string;
  header: ReactNode;
  width?: string | number; // fixed, non-growing
  /** Column may grow/shrink but never below this. Additive — no effect on columns that omit it. */
  minWidth?: number;
  /** Column may grow but never above this. Additive — no effect on columns that omit it. */
  maxWidth?: number;
  align?: 'left' | 'right' | 'center';
  render: (item: T) => ReactNode;
}

export interface VirtualizedTableProps<T> {
  columns: VirtualizedTableColumn<T>[];
  items: T[];
  rowKey: (item: T) => string | number;
  rowHeight?: number;
  emptyState?: ReactNode;
  stickyHeader?: boolean;
  height?: string | number;
  /**
   * Minimum total width of the header + rows, in px — below this the table scrolls
   * horizontally instead of clipping columns. Omitted: rows stretch to 100% of the container.
   */
  minWidth?: number;
  /**
   * Opt-in row tap target — exception to the "no onRowClick" house rule (see file doc-comment)
   * for purpose-built touch/tablet flows only. Omitted: callers render an explicit action column.
   */
  onRowClick?: (item: T) => void;
  /** Accessible name for a clickable row (screen readers / keyboard nav). Required when `onRowClick` is set. */
  getRowLabel?: (item: T) => string;
}

const alignToJustify = (align?: 'left' | 'right' | 'center') => {
  if (align === 'right') return 'flex-end';
  if (align === 'center') return 'center';
  return 'flex-start';
};

const alignToTextAlign = (align?: 'left' | 'right' | 'center') =>
  align ?? 'left';

interface VirtualizedTableRowProps<T> {
  item: T;
  dataIndex: number;
  columns: VirtualizedTableColumn<T>[];
  cellStyle: (column: VirtualizedTableColumn<T>) => CSSProperties;
  style: CSSProperties;
  onRowClick?: (item: T) => void;
  getRowLabel?: (item: T) => string;
}

const VirtualizedTableRow = <T,>({
  item,
  dataIndex,
  columns,
  cellStyle,
  style,
  onRowClick,
  getRowLabel,
}: VirtualizedTableRowProps<T>) => {
  const [isHovered, setIsHovered] = useState(false);
  const clickable = onRowClick !== undefined;

  return (
    <Box
      role="row"
      data-index={dataIndex}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? getRowLabel?.(item) : undefined}
      onClick={clickable ? () => onRowClick(item) : undefined}
      onKeyDown={
        clickable
          ? event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onRowClick(item);
              }
            }
          : undefined
      }
      onMouseEnter={clickable ? () => setIsHovered(true) : undefined}
      onMouseLeave={clickable ? () => setIsHovered(false) : undefined}
      style={{
        ...style,
        cursor: clickable ? 'pointer' : undefined,
        background: clickable && isHovered ? 'var(--mantine-color-gray-0)' : undefined,
      }}>
      {columns.map(column => (
        <Box key={column.id} role="cell" style={cellStyle(column)}>
          {column.render(item)}
        </Box>
      ))}
    </Box>
  );
};

export const VirtualizedTable = <T,>({
  columns,
  items,
  rowKey,
  rowHeight = 56,
  emptyState,
  stickyHeader = false,
  height = '100%',
  minWidth,
  onRowClick,
  getRowLabel,
}: VirtualizedTableProps<T>) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });

  const virtualRows = virtualizer.getVirtualItems();

  const cellStyle = (column: VirtualizedTableColumn<T>) => ({
    flex: column.width === undefined ? '1 1 0' : `0 0 ${
      typeof column.width === 'number' ? `${column.width}px` : column.width
    }`,
    minWidth: column.minWidth ?? (column.width === undefined ? 0 : undefined),
    maxWidth: column.maxWidth,
    display: 'flex',
    alignItems: 'center',
    justifyContent: alignToJustify(column.align),
    textAlign: alignToTextAlign(column.align) as
      | 'left'
      | 'right'
      | 'center',
    padding: '0 12px',
  });

  return (
    <Box
      ref={parentRef}
      role="table"
      style={{
        height,
        overflow: 'auto',
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-sm)',
      }}>
      <Box style={{minWidth}}>
        <Box
          role="row"
          style={{
            display: 'flex',
            position: stickyHeader ? 'sticky' : 'static',
            top: 0,
            zIndex: 1,
            background: 'var(--mantine-color-body)',
            borderBottom: '1px solid var(--mantine-color-gray-3)',
            fontWeight: 600,
            fontSize: 'var(--mantine-font-size-sm)',
            minHeight: rowHeight,
          }}>
          {columns.map(column => (
            <Box key={column.id} role="columnheader" style={cellStyle(column)}>
              {column.header}
            </Box>
          ))}
        </Box>

        {items.length === 0 ? (
          <Box p="xl">{emptyState}</Box>
        ) : (
          <Box
            role="rowgroup"
            style={{
              height: virtualizer.getTotalSize(),
              position: 'relative',
            }}>
            {virtualRows.map(virtualRow => {
              const item = items[virtualRow.index];
              return (
                <VirtualizedTableRow
                  key={rowKey(item)}
                  item={item}
                  dataIndex={virtualRow.index}
                  columns={columns}
                  cellStyle={cellStyle}
                  onRowClick={onRowClick}
                  getRowLabel={getRowLabel}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    display: 'flex',
                    height: virtualRow.size,
                    transform: `translateY(${virtualRow.start}px)`,
                    borderBottom: '1px solid var(--mantine-color-gray-2)',
                  }}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};
